import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ArrowBack, Add, Delete } from '@mui/icons-material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import useRecipeV2Api from './hooks/useRecipeV2Api';
import {
  CreateRecipeRequest,
  CreateComponentRequest,
  ProductionMethod,
  StockItemSearchResult,
} from './types';

const STEPS = ['create.step1', 'create.step2', 'create.step3'] as const;

const RECIPE_CATEGORIES = ['Bakery', 'Desserts', 'Drinks', 'Frozen', 'Mains', 'Sauces', 'Sides'] as const;

const RecipeV2CreatePage = () => {
  const { t } = useTranslation('recipeV2');
  const navigate = useNavigate();
  const theme = useTheme();
  const api = useRecipeV2Api();

  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Step 1 fields
  const [description, setDescription] = useState('');
  const [itemNumber, setItemNumber] = useState('');
  const [primaryCategory, setPrimaryCategory] = useState('');
  const [yieldQuantity, setYieldQuantity] = useState<number>(1);
  const [yieldUom, setYieldUom] = useState('Portions');
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().split('T')[0]);
  const [effectiveTo, setEffectiveTo] = useState('');
  const [productionMethodId, setProductionMethodId] = useState<number | null>(null);
  const [productionMethods, setProductionMethods] = useState<ProductionMethod[]>([]);

  // Step 2 fields
  const [components, setComponents] = useState<CreateComponentRequest[]>([]);
  const [searchResults, setSearchResults] = useState<StockItemSearchResult[]>([]);
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    api.getProductionMethods()
      .then(setProductionMethods)
      .catch(() => setProductionMethods([]));
  }, [api]);

  const handleSearchItems = useCallback(async (value: string) => {
    if (value.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const results = await api.searchStockItems(value);
      setSearchResults(results);
    } catch {
      setSearchResults([]);
    }
  }, [api]);

  const handleAddComponent = (item: StockItemSearchResult | null) => {
    if (!item) return;
    const exists = components.some((c) => c.componentProductId === item.id);
    if (exists) return;

    setComponents((prev) => [
      ...prev,
      {
        componentProductId: item.id,
        componentItemNumber: item.itemNumber,
        componentDescription: item.description,
        quantity: 1,
        weight: item.weightNet,
        volume: 0,
        uom: item.uom,
        sortOrder: prev.length + 1,
      },
    ]);
    setSearchInput('');
    setSearchResults([]);
  };

  const handleRemoveComponent = (productId: number) => {
    setComponents((prev) =>
      prev
        .filter((c) => c.componentProductId !== productId)
        .map((c, i) => ({ ...c, sortOrder: i + 1 })),
    );
  };

  const handleComponentFieldChange = (productId: number, field: keyof CreateComponentRequest, value: number) => {
    setComponents((prev) =>
      prev.map((c) => (c.componentProductId === productId ? { ...c, [field]: value } : c)),
    );
  };

  const isStep1Valid = description.trim() !== '' && itemNumber.trim() !== '' && primaryCategory.trim() !== '';

  const handleCreate = async () => {
    setSaving(true);
    try {
      const request: CreateRecipeRequest = {
        description,
        itemNumber,
        primaryCategory,
        yieldQuantity,
        yieldUom,
        effectiveFrom,
        effectiveTo: effectiveTo || null,
        productionMethodId,
        components,
      };
      const created = await api.createRecipe(request);
      navigate(`/store/recipes-v2/${created.id}`);
    } catch {
      setSaving(false);
    }
  };

  return (
    <PageArea>
      <Stack spacing={3}
        width="100%">
        {/* Header */}
        <Stack direction="row"
          alignItems="center"
          spacing={1}>
          <IconButton onClick={() => navigate('/store/recipes-v2')}
            size="small">
            <ArrowBack />
          </IconButton>
          <PageTitle>{t('create.title')}</PageTitle>
        </Stack>

        {/* Stepper */}
        <Stepper activeStep={activeStep}
          sx={{ pb: 2 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{t(label)}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 1: Metadata */}
        {activeStep === 0 && (
          <Section style={{ p: 5 }}>
            <Grid container
              spacing={4}>
              <Grid item
                xs={12}>
                <TextField
                  label={t('create.description')}
                  placeholder={t('create.descriptionPlaceholder')}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('create.itemNumber')}
                  placeholder={t('create.itemNumberPlaceholder')}
                  value={itemNumber}
                  onChange={(e) => setItemNumber(e.target.value)}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('create.category')}
                  select
                  value={primaryCategory}
                  onChange={(e) => setPrimaryCategory(e.target.value)}
                  required
                  fullWidth
                >
                  <MenuItem value=""
                    disabled>
                    <em>{t('create.categoryPlaceholder')}</em>
                  </MenuItem>
                  {RECIPE_CATEGORIES.map((category) => (
                    <MenuItem key={category}
                      value={category}>{category}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('create.yieldQuantity')}
                  type="number"
                  value={yieldQuantity}
                  onChange={(e) => setYieldQuantity(Number(e.target.value) || 1)}
                  inputProps={{ min: 1 }}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('create.yieldUom')}
                  value={yieldUom}
                  onChange={(e) => setYieldUom(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('create.effectiveFrom')}
                  type="date"
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('create.effectiveTo')}
                  type="date"
                  value={effectiveTo}
                  onChange={(e) => setEffectiveTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('create.productionMethod')}
                  select
                  value={productionMethodId ?? ''}
                  onChange={(e) => setProductionMethodId(e.target.value ? Number(e.target.value) : null)}
                  fullWidth
                >
                  <MenuItem value="">{t('create.none')}</MenuItem>
                  {productionMethods.map((pm) => (
                    <MenuItem key={pm.id}
                      value={pm.id}>{pm.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Section>
        )}

        {/* Step 2: Add Components */}
        {activeStep === 1 && (
          <Section style={{ p: 5 }}>
            <Stack spacing={3}>
              <Autocomplete
                options={searchResults}
                getOptionLabel={(o) => `${o.itemNumber} — ${o.description}`}
                inputValue={searchInput}
                onInputChange={(_, value) => {
                  setSearchInput(value);
                  handleSearchItems(value);
                }}
                onChange={(_, value) => handleAddComponent(value)}
                renderInput={(params) => (
                  <TextField {...params}
                    label={t('build.addComponent')}
                    placeholder={t('build.searchComponents')}
                  />
                )}
                noOptionsText={searchInput.length < 2 ? 'Type to search...' : 'No results'}
                value={null}
                blurOnSelect
              />

              {components.length > 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>{t('name')}</TableCell>
                        <TableCell>{t('sku')}</TableCell>
                        <TableCell align="right">{t('quantity')}</TableCell>
                        <TableCell align="right">{t('build.weight')}</TableCell>
                        <TableCell align="center">{t('build.uom')}</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {components.map((c, idx) => (
                        <TableRow key={c.componentProductId}>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{c.componentDescription}</TableCell>
                          <TableCell>
                            <Typography variant="textXS"
                              fontFamily="monospace">{c.componentItemNumber}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={c.quantity}
                              onChange={(e) => handleComponentFieldChange(c.componentProductId, 'quantity', parseFloat(e.target.value) || 0)}
                              sx={{ width: 80 }}
                              inputProps={{ step: 0.01, min: 0 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={c.weight}
                              onChange={(e) => handleComponentFieldChange(c.componentProductId, 'weight', parseFloat(e.target.value) || 0)}
                              sx={{ width: 80 }}
                              inputProps={{ min: 0 }}
                            />
                          </TableCell>
                          <TableCell align="center">{c.uom}</TableCell>
                          <TableCell>
                            <IconButton size="small"
                              onClick={() => handleRemoveComponent(c.componentProductId)}
                              color="error">
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {components.length === 0 && (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">{t('build.searchComponents')}</Typography>
                </Box>
              )}
            </Stack>
          </Section>
        )}

        {/* Step 3: Review */}
        {activeStep === 2 && (
          <Section style={{ p: 5 }}>
            <Stack spacing={4}>
              <Typography variant="textXL">{t('create.reviewTitle')}</Typography>
              <Grid container
                spacing={3}>
                {[
                  { label: t('create.description'), value: description },
                  { label: t('create.itemNumber'), value: itemNumber, mono: true },
                  { label: t('create.category'), value: primaryCategory },
                  { label: t('yield'), value: `${yieldQuantity} ${yieldUom}` },
                  { label: t('dateRange'), value: `${effectiveFrom}${effectiveTo ? ` → ${effectiveTo}` : ''}` },
                ].map((field) => (
                  <Grid item
                    xs={12}
                    sm={6}
                    md={4}
                    key={field.label}>
                    <Box
                      sx={{
                        p: 3,
                        border: '1px solid',
                        borderColor: theme.palette.custom.gray[300],
                        borderRadius: '12px',
                      }}
                    >
                      <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.5, fontWeight: 500 }}>
                        {field.label}
                      </Box>
                      <Box sx={{ fontSize: '16px', fontWeight: 600, color: theme.palette.custom.gray[900], fontFamily: field.mono ? 'monospace' : undefined }}>
                        {field.value || t('create.none')}
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Typography variant="textLG">
                {t('create.reviewComponents', { count: components.length })}
              </Typography>
              {components.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('name')}</TableCell>
                        <TableCell>{t('sku')}</TableCell>
                        <TableCell align="right">{t('quantity')}</TableCell>
                        <TableCell align="right">{t('build.weight')}</TableCell>
                        <TableCell align="center">{t('build.uom')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {components.map((c) => (
                        <TableRow key={c.componentProductId}>
                          <TableCell>{c.componentDescription}</TableCell>
                          <TableCell>{c.componentItemNumber}</TableCell>
                          <TableCell align="right">{c.quantity}</TableCell>
                          <TableCell align="right">{c.weight}g</TableCell>
                          <TableCell align="center">{c.uom}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Chip label="No components added" />
              )}
            </Stack>
          </Section>
        )}

        {/* Navigation buttons */}
        <Stack direction="row"
          justifyContent="space-between">
          <Button
            variant="secondary"
            disabled={activeStep === 0}
            onClick={() => setActiveStep((s) => s - 1)}
          >
            {t('create.previous')}
          </Button>
          {activeStep < 2 ? (
            <Button
              variant="primary"
              disabled={activeStep === 0 && !isStep1Valid}
              onClick={() => setActiveStep((s) => s + 1)}
            >
              {t('create.next')}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={saving}
            >
              {saving ? '...' : t('create.createRecipe')}
            </Button>
          )}
        </Stack>
      </Stack>
    </PageArea>
  );
};

export default RecipeV2CreatePage;
